import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useApplyPhaseTemplate } from "../../../../../hooks/annual-planner/useApplyPhaseTemplate";
import useDropdownOverlay, {
  getOverlayStyle,
} from "../../../../../hooks/useDropdownOverlay";
import ConfirmationModal from "../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../parts/SvgIcon";

const CHECK_ICON = "/img/new-icons/vuesax-linear-tick-no-background.svg";

// Built directly on useDropdownOverlay rather than the higher-level
// DropdownActions component: DropdownActions' options are a flat, uniform
// list (label + optional edit icon), with no slot for a section header, a
// divider, or a per-row checkmark, and extending a component shared with
// the Nutrition Table's own template switcher for this one caller's shape
// felt riskier than just rendering custom markup around the same
// underlying open/close/position hook DropdownActions itself is built on.
//
// Applying a template on click, "save current plan as template", and "set
// my default template" all go through useApplyPhaseTemplate (shared with
// phase-templates-modal.js) and the existing Add Template modal-open route.
export const PhaseTemplateDropdown = ({
  templates,
  currentTemplateId,
  isCurrentTheDefault,
  athleteId,
  organizationCoachId,
  hasDatedPhases,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { opened, toggle, close, summaryRef, listRef, listPosition } =
    useDropdownOverlay({ overlay: true, remeasureKey: templates });

  const {
    pendingTemplate,
    requestApplyTemplate,
    confirmPendingTemplate,
    cancelPendingTemplate,
    setAsDefaultTemplate,
  } = useApplyPhaseTemplate({ athleteId, organizationCoachId, hasDatedPhases });

  const currentTemplate = templates.find(
    (template) => template._id === currentTemplateId
  );

  const handleTemplateClick = (template) => {
    close();
    // Already applied - nothing to do, don't even ask the server.
    if (template._id === currentTemplateId) return;
    requestApplyTemplate(template);
  };

  const openSaveCurrentPlanModal = () => {
    close();
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "add-phase-template", seedFromAthlete: athleteId },
    });
  };

  const handleSetDefaultClick = () => {
    close();
    setAsDefaultTemplate(currentTemplate);
  };

  const panel = (
    <div
      ref={listRef}
      className="phase-template-dropdown__panel"
      style={getOverlayStyle(listPosition)}
    >
      <span className="phase-template-dropdown__section-label">
        {t("annualPlanner.applyPhaseTemplate")}
      </span>
      <ul role="listbox">
        {templates.map((template) => (
          <li key={template._id}>
            <div //NOSONAR
              role="option"
              aria-selected={template._id === currentTemplateId}
              className="phase-template-dropdown__row"
              onMouseDown={() => handleTemplateClick(template)}
            >
              <span>{template.templateName}</span>
              {template._id === currentTemplateId && (
                <img src={CHECK_ICON} alt="" />
              )}
            </div>
          </li>
        ))}
        <li>
          <button
            type="button"
            className="phase-template-dropdown__action"
            onMouseDown={openSaveCurrentPlanModal}
          >
            <span className="clipped">
              {t("annualPlanner.saveCurrentPlanAsTemplate")}
            </span>
          </button>
        </li>
        {currentTemplate && !isCurrentTheDefault && (
          <li>
            <button
              type="button"
              className="phase-template-dropdown__action"
              onMouseDown={handleSetDefaultClick}
            >
              <span className="clipped">
                {t("annualPlanner.setMyDefaultTemplate")}
              </span>
            </button>
          </li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="phase-template-dropdown">
      <button
        type="button"
        ref={summaryRef}
        className="phase-template-dropdown__trigger"
        onClick={toggle}
      >
        <span>
          {currentTemplate?.templateName || t("annualPlanner.defaultView")}
        </span>
        <SvgIcon
          src={opened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
        />
      </button>
      {opened && createPortal(panel, document.body)}
      {pendingTemplate && (
        <ConfirmationModal
          isOpen
          className="annual-planner-modal phase-templates-modal warning"
          badgeText={t("annualPlanner.warningLabel")}
          title={t("annualPlanner.changeTemplateTitle")}
          contentText={t("annualPlanner.warningMessage")}
          cancelButtonText={t("common:cancel")}
          cancelButtonAction={cancelPendingTemplate}
          confirmButtonText={t("annualPlanner.changeButton")}
          confirmButtonAction={confirmPendingTemplate}
          confirmSuccess
        />
      )}
    </div>
  );
};
